import { COLORS } from 'consts/colors'
import { css } from 'styled-components'

/**
 * Tokenized geometry for the 2026 indigo design system.
 * field  — inputs, buttons, dropdown triggers, tabs
 * card   — menus, tiles, popovers, selection rings, rails
 * popup  — modals (mobile SlideUp uses `16px 16px 0 0`)
 * item   — menu-item pills, small controls
 * pill   — toggles, category pills, circular buttons
 */
export const RADII = {
  field: '8px',
  card: '12px',
  popup: '16px',
  item: '8px',
  pill: '999px'
}

/** Neutral shadow ramp (no blue tint). */
export const SHADOWS = {
  menu: '0px 4px 16px rgba(20, 20, 43, 0.1)',
  card: '0px 2px 8px rgba(20, 20, 43, 0.06)',
  popup: '0px 12px 32px rgba(20, 20, 43, 0.16)',
  /* Soft diffuse elevation for the floating shell panels (sidebar, subsidebar,
     canvas column) — replaces the hard 1px panel borders. */
  panel: '0px 4px 20px rgba(20, 20, 43, 0.08)'
}

/** Standard control sizing. 40px default, 32px for dense editor chrome. */
export const CONTROL = {
  height: '40px',
  heightCompact: '32px',
  iconSize: '20px'
}

/**
 * Motion tokens. UI motion is fast and ease-out; ~300ms is the ceiling for
 * interface elements. Transition individual properties — never `all`.
 * out    — enter/exit, hover, press feedback (the default)
 * inOut  — on-screen movement (sliding pills, reordering)
 * drawer — large surfaces sliding in (sheets, flyouts)
 */
export const EASING = {
  out: 'cubic-bezier(0.23, 1, 0.32, 1)',
  inOut: 'cubic-bezier(0.77, 0, 0.175, 1)',
  drawer: 'cubic-bezier(0.32, 0.72, 0, 1)'
}

export const DURATIONS = {
  fast: '120ms',
  base: '180ms',
  slow: '240ms'
}

/** framer-motion equivalents of the CSS motion tokens (seconds / bezier arrays). */
export const MOTION = {
  duration: { fast: 0.12, base: 0.18, slow: 0.24 },
  ease: {
    out: [0.23, 1, 0.32, 1] as [number, number, number, number],
    inOut: [0.77, 0, 0.175, 1] as [number, number, number, number],
    drawer: [0.32, 0.72, 0, 1] as [number, number, number, number]
  }
}

/**
 * GenAI / premium brand gradient, shared with ai-video-generator's premium
 * CTAs. Hover reverses the stop order.
 */
export const GRADIENTS = {
  premium:
    'linear-gradient(261.91deg, #2214e4 -3.09%, #d696ce 47.21%, #2f67fe 93.65%)',
  premiumHover:
    'linear-gradient(261.91deg, #2f67fe -3.09%, #d696ce 43.34%, #2214e4 93.65%)',
  /* Pink/magenta credits + unlimited branding, shared with the homepage header. */
  pinkMagenta: `linear-gradient(90deg, ${COLORS.magenta} 0%, ${COLORS.magentaLight} 100%)`,
  pinkMagentaBorder: `linear-gradient(180deg, ${COLORS.magenta} 0%, ${COLORS.magentaMid} 48.99%, ${COLORS.magentaLight} 100%)`
}

export const BOX_SHADOWS = {
  previewAreaShadow: css`
    box-shadow: ${SHADOWS.menu};
  `,
  shadow1: css`
    box-shadow: ${SHADOWS.card};
  `,
  shadow2: css`
    box-shadow: ${SHADOWS.menu};
  `,
  mobileExpandShadow: css`
    box-shadow: 0px -4px 16px rgba(20, 20, 43, 0.1);
  `,
  darkShadow: css`
    box-shadow: 0px 2px 8px rgba(20, 20, 43, 0.2);
  `,
  headerShadow: css`
    box-shadow: ${SHADOWS.card};
  `,

  timelineShadow: css`
    box-shadow: 0px -2px 8px rgba(20, 20, 43, 0.06);
  `
}

export const ChessGridBackground = css`
  background-image: repeating-conic-gradient(
    ${COLORS.primaryExtraLight} 0 25%,
    ${COLORS.borderColor} 0 50%
  );
  background-size: 23px 23px;
`
